import React from 'react';
import { useTranslation } from 'react-i18next';

const AVAILABLE_TOOLS = [
  {
    id: 'getCalendarSlots',
    labelKey: 'toolsList.getCalendarSlots',
    icon: '📅',
    categoryKey: 'toolsList.categories.calendar',
    descriptionKey: 'toolsList.descriptions.getCalendarSlots',
  },
  {
    id: 'createBooking',
    labelKey: 'toolsList.createBooking',
    icon: '✅',
    categoryKey: 'toolsList.categories.calendar',
    descriptionKey: 'toolsList.descriptions.createBooking',
  },
  {
    id: 'cancelBooking',
    labelKey: 'toolsList.cancelBooking',
    icon: '❌',
    categoryKey: 'toolsList.categories.calendar',
    descriptionKey: 'toolsList.descriptions.cancelBooking',
  },
  {
    // Re-exposed: handleSendEmail now sends for real (SMTP + Gmail). When no mail
    // provider is connected the executor returns an honest error (NoopMail) and the
    // prompt's anti-hallucination + "SIN ENVÍO DE EMAIL" rules prevent false claims —
    // same gating model as the calendar tools (enforced at runtime/prompt, not here).
    id: 'sendEmail',
    labelKey: 'toolsList.sendEmail',
    icon: '✉️',
    categoryKey: 'toolsList.categories.email',
    descriptionKey: 'toolsList.descriptions.sendEmail',
  },
  {
    id: 'searchCatalog',
    labelKey: 'toolsList.searchCatalog',
    icon: '🔍',
    categoryKey: 'toolsList.categories.ecommerce',
    descriptionKey: 'toolsList.descriptions.searchCatalog',
  },
  {
    id: 'getProduct',
    labelKey: 'toolsList.getProduct',
    icon: '📦',
    categoryKey: 'toolsList.categories.ecommerce',
    descriptionKey: 'toolsList.descriptions.getProduct',
  },
  {
    id: 'createCheckoutLink',
    labelKey: 'toolsList.createCheckoutLink',
    icon: '💳',
    categoryKey: 'toolsList.categories.ecommerce',
    descriptionKey: 'toolsList.descriptions.createCheckoutLink',
  },
  {
    id: 'getOrderStatus',
    labelKey: 'toolsList.getOrderStatus',
    icon: '📋',
    categoryKey: 'toolsList.categories.ecommerce',
    descriptionKey: 'toolsList.descriptions.getOrderStatus',
  },
  {
    id: 'repoListDocs',
    labelKey: 'toolsList.repoListDocs',
    icon: '📚',
    categoryKey: 'toolsList.categories.repository',
    descriptionKey: 'toolsList.descriptions.repoListDocs',
    disabled: false,
  },
  {
    id: 'repoSearch',
    labelKey: 'toolsList.repoSearch',
    icon: '🔎',
    categoryKey: 'toolsList.categories.repository',
    descriptionKey: 'toolsList.descriptions.repoSearch',
    disabled: false,
  },
  // C6 (28-oct-2026): mandar los archivos de la biblioteca de medios (pestaña Repositorio). Sólo se
  // le ofrece al modelo en WhatsApp Cloud API y si la biblioteca tiene archivos.
  {
    id: 'enviarArchivo',
    labelKey: 'toolsList.enviarArchivo',
    icon: '📎',
    categoryKey: 'toolsList.categories.media',
    descriptionKey: 'toolsList.descriptions.enviarArchivo',
    disabled: false,
  },
];

export default function ToolsAllowlist({ allowed = [], onChange }) {
  const { t } = useTranslation('bots');

  const toggleTool = (toolId) => {
    if (allowed.includes(toolId)) {
      onChange(allowed.filter(t => t !== toolId));
    } else {
      onChange([...allowed, toolId]);
    }
  };

  const categories = [...new Set(AVAILABLE_TOOLS.map(tool => t(tool.categoryKey)))];

  return (
    <div className="tools-allowlist">
      {categories.map(category => {
        const categoryTools = AVAILABLE_TOOLS.filter(tool => t(tool.categoryKey) === category);

        return (
          <div key={category} className="category-section">
            <h3 className="category-title">{category}</h3>
            <div className="tools-grid">
              {categoryTools.map(tool => (
                <div
                  key={tool.id}
                  className={`tool-card ${allowed.includes(tool.id) ? 'selected' : ''} ${tool.disabled ? 'disabled' : ''}`}
                  onClick={() => !tool.disabled && toggleTool(tool.id)}
                >
                  <div className="tool-header">
                    <span className="tool-icon">{tool.icon}</span>
                    <span className="tool-label">{t(tool.labelKey)}</span>
                  </div>
                  <p className="tool-description">{t(tool.descriptionKey)}</p>
                  {tool.disabled && (
                    <span className="coming-soon">{t('toolsList.comingSoon')}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}

      <style>{`
        .tools-allowlist {
          margin-top: 1rem;
        }

        .category-section {
          margin-bottom: 2rem;
        }

        .category-title {
          font-size: 1rem;
          font-weight: 600;
          color: #2d3748;
          margin-bottom: 1rem;
          padding-bottom: 0.5rem;
          border-bottom: 2px solid #e2e8f0;
        }

        .tools-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
          gap: 1rem;
        }

        .tool-card {
          padding: 1rem;
          border: 2px solid #e2e8f0;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s;
          background: white;
        }

        .tool-card:not(.disabled):hover {
          border-color: #667eea;
          transform: translateY(-2px);
          box-shadow: 0 4px 6px rgba(0,0,0,0.1);
        }

        .tool-card.selected {
          border-color: #48bb78;
          background: #f0fff4;
        }

        .tool-card.disabled {
          opacity: 0.5;
          cursor: not-allowed;
          background: #f7fafc;
        }

        .tool-header {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-bottom: 0.5rem;
        }

        .tool-icon {
          font-size: 1.5rem;
        }

        .tool-label {
          font-weight: 600;
          color: #2d3748;
          font-size: 0.9rem;
        }

        .tool-description {
          font-size: 0.8rem;
          color: #718096;
          margin: 0;
        }

        .coming-soon {
          display: inline-block;
          margin-top: 0.5rem;
          padding: 0.25rem 0.5rem;
          background: #feebc8;
          color: #7c2d12;
          border-radius: 4px;
          font-size: 0.75rem;
          font-weight: 600;
        }
      `}</style>
    </div>
  );
}
